import { createSelector } from 'reselect'
import { pathOr } from 'ramda'
import { initialState } from './reducer'
import { DateTime } from 'luxon'
import { formatDate } from 'utils/dateUtils'
import { BILLING_PROVIDER_SETTINGS } from 'config'

/**
 * Direct selector to the registration state domain
 */
export const selectSignUp = state => state.signUp

/**
 * Other specific selectors
 */

export const makeSelectError = () =>
  createSelector(selectSignUp, substate =>
    pathOr(initialState.error, ['error'], substate)
  )

export const makeSelectIsLoading = () =>
  createSelector(selectSignUp, substate =>
    pathOr(initialState.loading, ['loading'], substate)
  )

export const makeSelectIsGeneralEnrollment = () =>
  createSelector(selectSignUp, substate =>
    pathOr(initialState.isGeneralEnrollment, ['isGeneralEnrollment'], substate)
  )

export const makeSelectMeterID = () =>
  createSelector(selectSignUp, substate =>
    pathOr(initialState.meterID, ['meterID'], substate)
  )

export const makeSelectDepositAmount = () =>
  createSelector(selectSignUp, substate =>
    pathOr(250, ['depositAmount'], substate)
  )

export const makeSelectAutoPay = () =>
  createSelector(selectSignUp, substate =>
    pathOr(250, ['enrollment', 'formData', 'autopay'], substate)
  )

export const makeSelectPaymentForm = () =>
  createSelector(selectSignUp, substate =>
    pathOr(false, ['paymentForm'], substate)
  )

export const makeSelectIsLogin = () =>
  createSelector(selectSignUp, substate => pathOr(false, ['login'], substate))

export const makeSelectUserAlreadyExists = () =>
  createSelector(selectSignUp, substate =>
    pathOr(false, ['userAlreadyExists'], substate)
  )

export const makeSelectIsInValidProduct = () =>
  createSelector(selectSignUp, substate =>
    pathOr(false, ['inValidProduct'], substate)
  )

export const makeSelectMeterAlreadyConnected = () =>
  createSelector(selectSignUp, substate =>
    pathOr(false, ['meterAlreadyConnected'], substate)
  )

export const makeSelectLockCreditCheckInfo = () =>
  createSelector(selectSignUp, substate =>
    pathOr(false, ['lockCreditCheckInfo'], substate)
  )

export const makeSelectReferralInfo = () =>
  createSelector(selectSignUp, substate =>
    pathOr(initialState.referralInfo, ['referralInfo'], substate)
  )

export const makeSelectMetersForLocation = () =>
  createSelector(selectSignUp, substate =>
    pathOr(initialState.metersForLocation, ['metersForLocation'], substate)
  )

export const makeSelectMeterOverride = () =>
  createSelector(selectSignUp, substate =>
    pathOr(false, ['meterOverride'], substate)
  )

export const makeSelectUserLocation = () =>
  createSelector(
    makeSelectMetersForLocation(),
    makeSelectMeterID(),
    (locations, esi) => locations.find(location => location.esi === esi)
  )

export const makeSelectIsCommercial = () =>
  createSelector(
    makeSelectUserLocation(),
    substate => pathOr(false, ['griddy_rate_class'], substate) === 'commercial'
  )

export const makeSelectIsResidential = () =>
  createSelector(
    makeSelectUserLocation(),
    substate => pathOr(false, ['griddy_rate_class'], substate) === 'residential'
  )

export const makeSelectServiceInformation = () =>
  createSelector(selectSignUp, substate =>
    pathOr(initialState.serviceInformation, ['serviceInformation'], substate)
  )

export const makeSelectIsMoveIn = () =>
  createSelector(
    makeSelectServiceInformation(),
    substate => pathOr(false, ['enrollMode'], substate) === 'movein'
  )

export const makeSelectStartDate = () =>
  createSelector(makeSelectServiceInformation(), substate => {
    const date = pathOr(DateTime.local(), ['requestedStartDate'], substate)
    return formatDate(date, 'LL/dd/yyyy')
  })

export const makeSelectFirstAvailableDate = () =>
  createSelector(makeSelectServiceInformation(), substate =>
    pathOr(false, ['firstAvailableDate'], substate)
  )

export const makeSelectRechargeAmount = () =>
  createSelector(selectSignUp, substate =>
    pathOr(initialState.rechargeAmount, ['rechargeAmount'], substate)
  )

export const makeSelectSummerLock = () =>
  createSelector(selectSignUp, substate =>
    pathOr(initialState.summerLock, ['summerLock'], substate)
  )

export const makeSelectSummerLockUpdated = () =>
  createSelector(selectSignUp, substate =>
    pathOr(initialState.summerLockUpdated, ['summerLockUpdated'], substate)
  )

export const makeSelectPersonalInformation = () =>
  createSelector(selectSignUp, substate =>
    pathOr(initialState.personalInformation, ['personalInformation'], substate)
  )

export const makeSelectFirstName = () =>
  createSelector(makeSelectPersonalInformation(), substate =>
    pathOr('', ['first_name'], substate)
  )

export const makeSelectServiceAddress = () =>
  createSelector(selectSignUp, substate =>
    pathOr(initialState.serviceAddress, ['serviceAddress'], substate)
  )

export const makeSelectServiceAddressZipcode = () =>
  createSelector(makeSelectServiceAddress(), serviceAddres =>
    pathOr('', ['postal_code'], serviceAddres)
  )

export const selectServiceAddress = () =>
  createSelector(makeSelectServiceAddress(), serviceAddress => {
    return `${serviceAddress.line1} ${serviceAddress.line2} ${serviceAddress.city}, ${serviceAddress.state} ${serviceAddress.postal_code}`
  })

export const makeSelectShouldRedirectToPayment = () =>
  createSelector(selectSignUp, substate =>
    pathOr(
      initialState.shouldRedirectToPayment,
      ['shouldRedirectToPayment'],
      substate
    )
  )

export const selectIsFinished = createSelector(
  selectSignUp,
  substate => substate.finished
)

export const makeSelectTdsps = () =>
  createSelector(selectSignUp, substate =>
    pathOr([], ['tdspsWithOffers'], substate)
  )

export const makeSelectEnrollment = () =>
  createSelector(selectSignUp, substate =>
    pathOr(initialState.enrollment, ['enrollment'], substate)
  )

export const makeSelectBills = () =>
  createSelector(selectSignUp, substate => pathOr([], ['bills'], substate))

export const makeSelectSignature = () =>
  createSelector(selectSignUp, substate => pathOr([], ['signature'], substate))

export const makeSelectDocuments = () =>
  createSelector(selectSignUp, substate => pathOr([], ['documents'], substate))

export const makeSelectEnrollmentType = () =>
  createSelector(selectSignUp, substate =>
    pathOr(initialState.enrollment, ['enrollment', 'enrollmentType'], substate)
  )

export const makeSelectCompleteEnrollment = () =>
  createSelector(selectSignUp, substate =>
    pathOr(initialState.completeEnrollment, ['completeEnrollment'], substate)
  )

export const makeSelectEnrollmentEmail = () =>
  createSelector(selectSignUp, substate =>
    pathOr(250, ['enrollment', 'email'], substate)
  )

export const makeSelectFormData = () =>
  createSelector(makeSelectEnrollment(), enrollment =>
    pathOr(null, ['formData'], enrollment)
  )

export const makeSelectProduct = () =>
  createSelector(makeSelectFormData(), formData => {
    return pathOr('[]', ['product'], formData)
  })

export const makeSelectProductDocuments = () =>
  createSelector(makeSelectFormData(), formData => {
    return pathOr('[]', ['product', 'documents'], formData)
  })

export const makeSelectProductOptions = () =>
  createSelector(makeSelectFormData(), formData => {
    const options = pathOr('[]', ['product', 'options'], formData)

    try {
      // Parse the options here, assuming it's JSON
      const parsedData = JSON.parse(options)
      return { ...parsedData }
    } catch (error) {
      return {}
    }
  })

export const makeSelectProductOptionRaw = () =>
  createSelector(makeSelectFormData(), formData => {
    return pathOr('[]', ['product', 'options'], formData)
  })

export const makeSelectProductMaxDeposit = () =>
  createSelector(makeSelectFormData(), formData => {
    return pathOr({}, ['product', 'max_deposit'], formData)
  })

export const makeSelectUserAddress = () =>
  createSelector(makeSelectEnrollment(), enrollment =>
    pathOr(initialState.serviceAddress, ['formData', 'address'], enrollment)
  )
export const makeSelectTdsp = () =>
  createSelector(makeSelectEnrollment(), enrollment =>
    pathOr(null, ['formData', 'tdsp'], enrollment)
  )

export const makeSelectChasePaymentUID = () =>
  createSelector(makeSelectEnrollment(), substate =>
    pathOr('', ['formData', 'payment', 'token'], substate)
  )

export const makeSelectLanguage = () =>
  createSelector(makeSelectEnrollment(), substate =>
    pathOr('', ['formData', 'language'], substate)
  )

export const makeSelectBillingCycle = () =>
  createSelector(makeSelectEnrollment(), enrollment =>
    pathOr('1', ['formData', 'billingCycle'], enrollment)
  )

export const makeSelectNameKey = () =>
  createSelector(makeSelectEnrollment(), enrollment =>
    pathOr('', ['formData', 'nameKey'], enrollment)
  )

export const makeSelectmeterUID = () =>
  createSelector(makeSelectEnrollment(), enrollment =>
    pathOr('', ['formData', 'meterUID'], enrollment)
  )

export const makeSelectSalesChannelName = () =>
  createSelector(makeSelectEnrollment(), enrollment =>
    pathOr("", ['formData', 'salesChannelName'], enrollment)
  )

export const makeSelectSalesChannelCode = () =>
  createSelector(makeSelectEnrollment(), enrollment =>
    pathOr("", ['formData', 'salesChannelCode'], enrollment)
  )

export const makeSelectParameters = () =>
  createSelector(selectSignUp, substate =>
    pathOr(initialState.parameters, ['parameters'], substate)
  )

export const makeSelectParamMeterUID = () =>
  createSelector(makeSelectParameters(), parameters =>
    pathOr(null, ['meterIdentifier'], parameters)
  )

export const makeSelectParamMeterUID2 = () =>
  createSelector(makeSelectParameters(), parameters =>
    pathOr(null, ['meterIdentifier2'], parameters)
  )

export const makeSelectParamGUID = () =>
  createSelector(makeSelectParameters(), parameters =>
    pathOr(null, ['guid'], parameters)
  )

export const makeSelectParamPromoCode = () =>
  createSelector(makeSelectParameters(), parameters =>
    pathOr(null, ['promoCode'], parameters)
  )

export const makeSelectPremiseType = () =>
  createSelector(makeSelectEnrollment(), enrollment =>
    pathOr(null, ['formData', 'premise_type'], enrollment)
  )

export const makeSelectAvgMonthlyBill = () =>
  createSelector(makeSelectEnrollment(), enrollment =>
    pathOr(null, ['formData', 'avgMonthlyBill'], enrollment)
  )

export const makeSelectServiceType = () =>
  createSelector(selectSignUp, substate =>
    pathOr(250, ['enrollment', 'serviceType'], substate)
  )

export const makeSelectEnrollmentPhone = () =>
  createSelector(selectSignUp, substate =>
    pathOr(250, ['enrollment', 'phone'], substate)
  )

export const makeSelectBirthdate = () =>
  createSelector(makeSelectEnrollment(), enrollment =>
    pathOr(null, ['formData', 'birthdate'], enrollment)
  )

  export const makeSelectEnrollmentDocumentForSigning = () =>
    createSelector(selectSignUp, substate =>
      pathOr(null, ['documentSignatureUrl'], substate)
    )

export const makeSelectLMIProgramTypes = () =>
  createSelector(selectSignUp, substate =>
    pathOr([], ['lmiPrograms'], substate)
  )

export const makeSelectHideCreditCheck = () =>
  createSelector(makeSelectEnrollment(), enrollment =>
    pathOr('', ['formData', 'hide_credit_check'], enrollment)
  )

export const makeSelectIsLanguageDisabled = () =>
  createSelector(
    selectSignUp,
    substate => pathOr(false, ['isLanguageDisabled'], substate)
  )

export const makeSelectAgencyMemberId = () =>
  createSelector(makeSelectEnrollment(), enrollment =>
    pathOr("", ['formData', 'agency_member_id'], enrollment)
  )

export const makeSelectIsActiveAutoSave = () =>
  createSelector(selectSignUp, substate =>
    pathOr(false, ['isActiveAutoSave'], substate)
  )

 export const makeSelectBillingProvider = () => createSelector(
  makeSelectProductOptions(),
  substate => BILLING_PROVIDER_SETTINGS &&
  BILLING_PROVIDER_SETTINGS[substate?.billing_segment]
)

export const makeSelectAdditionalMeters = () =>
  createSelector(selectSignUp, substate =>
    pathOr([], ['additionalMeterInfo'], substate)
  )

/**
 * Default selector used by registration
 */

export default () => createSelector(selectSignUp, substate => substate)
